import AtriumApi from "api";

const teamService = {
  // ==========================================
  // Org Chart
  // ==========================================

  getOrgChart: async (params = {}) => {
    try {
      const response = await AtriumApi.get("/v2/team/org-chart", { params });
      return response.data;
    } catch (error) {
      console.error("Error fetching org chart:", error);
      throw error;
    }
  },

  getMembers: async () => {
    try {
      const response = await AtriumApi.get("/v2/team/members");
      return response.data;
    } catch (error) {
      console.error("Error fetching team members:", error);
      throw error;
    }
  },

  getMemberProfile: async (userId) => {
    try {
      const response = await AtriumApi.get(`/v2/team/members/${userId}/profile`);
      return response.data;
    } catch (error) {
      console.error("Error fetching member profile:", error);
      throw error;
    }
  },

  sendAssessment: async (userId, payload = {}) => {
    try {
      const response = await AtriumApi.post(`/v2/team/members/${userId}/send-assessment`, payload);
      return response.data;
    } catch (error) {
      console.error("Error sending assessment:", error);
      throw error;
    }
  },

  addNote: async (submissionId, text) => {
    try {
      const response = await AtriumApi.post(`/v2/team/check-ins/${submissionId}/notes`, { text });
      return response.data;
    } catch (error) {
      console.error("Error adding note:", error);
      throw error;
    }
  },

  setManager: async (userId, managerId) => {
    try {
      const response = await AtriumApi.put(`/v2/team/members/${userId}/manager`, { managerId });
      return response.data;
    } catch (error) {
      console.error("Error setting manager:", error);
      throw error;
    }
  },

  bulkAssign: async (assignments) => {
    try {
      const response = await AtriumApi.post("/v2/team/org-chart/bulk-assign", { assignments });
      return response.data;
    } catch (error) {
      console.error("Error bulk assigning managers:", error);
      throw error;
    }
  },

  // ==========================================
  // Check-in Questions (leader-authored)
  // ==========================================

  getQuestions: async (params = {}) => {
    try {
      const response = await AtriumApi.get("/v2/team/questions", { params });
      return response.data;
    } catch (error) {
      console.error("Error fetching check-in questions:", error);
      throw error;
    }
  },

  createQuestion: async (payload) => {
    try {
      const response = await AtriumApi.post("/v2/team/questions", payload);
      return response.data;
    } catch (error) {
      console.error("Error creating check-in question:", error);
      throw error;
    }
  },

  updateQuestion: async (id, payload) => {
    try {
      const response = await AtriumApi.put(`/v2/team/questions/${id}`, payload);
      return response.data;
    } catch (error) {
      console.error("Error updating check-in question:", error);
      throw error;
    }
  },

  suggestQuestions: async (payload = {}) => {
    try {
      const response = await AtriumApi.post("/v2/team/questions/suggest", payload);
      return response.data;
    } catch (error) {
      console.error("Error generating question suggestions:", error);
      throw error;
    }
  },

  deleteQuestion: async (id) => {
    try {
      const response = await AtriumApi.delete(`/v2/team/questions/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error deleting check-in question:", error);
      throw error;
    }
  },

  // ==========================================
  // Weekly Check-ins
  // ==========================================

  getMyCheckIn: async (params = {}) => {
    try {
      const response = await AtriumApi.get("/v2/team/check-ins/mine", { params });
      return response.data;
    } catch (error) {
      console.error("Error fetching my check-in:", error);
      throw error;
    }
  },

  submitCheckIn: async (payload) => {
    try {
      const response = await AtriumApi.post("/v2/team/check-ins", payload);
      return response.data;
    } catch (error) {
      console.error("Error submitting check-in:", error);
      throw error;
    }
  },

  getTeamStatus: async (params = {}) => {
    try {
      const response = await AtriumApi.get("/v2/team/check-ins/status", { params });
      return response.data;
    } catch (error) {
      console.error("Error fetching team status:", error);
      throw error;
    }
  },

  // ==========================================
  // Delivery (schedule, send-now, audit)
  // ==========================================

  getDeliverySettings: async () => {
    try {
      const response = await AtriumApi.get("/v2/team/delivery/settings");
      return response.data;
    } catch (error) {
      console.error("Error fetching delivery settings:", error);
      throw error;
    }
  },

  updateDeliverySettings: async (payload) => {
    try {
      const response = await AtriumApi.put("/v2/team/delivery/settings", payload);
      return response.data;
    } catch (error) {
      console.error("Error updating delivery settings:", error);
      throw error;
    }
  },

  getDeliveryStatus: async (params = {}) => {
    try {
      const response = await AtriumApi.get("/v2/team/delivery/status", { params });
      return response.data;
    } catch (error) {
      console.error("Error fetching delivery status:", error);
      throw error;
    }
  },

  sendNow: async (payload = {}) => {
    try {
      const response = await AtriumApi.post("/v2/team/delivery/send-now", payload);
      return response.data;
    } catch (error) {
      console.error("Error sending check-ins:", error);
      throw error;
    }
  },

  // ==========================================
  // Public token flow (no auth)
  // ==========================================

  getPublicCheckIn: async (token) => {
    try {
      const response = await AtriumApi.get(`/v2/team/public/check-in/${token}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching public check-in:", error);
      throw error;
    }
  },

  submitPublicCheckIn: async (token, answers) => {
    try {
      const response = await AtriumApi.post(`/v2/team/public/check-in/${token}`, { answers });
      return response.data;
    } catch (error) {
      console.error("Error submitting public check-in:", error);
      throw error;
    }
  },

  getUserHistory: async (userId, params = {}) => {
    try {
      const response = await AtriumApi.get(`/v2/team/check-ins/users/${userId}/history`, { params });
      return response.data;
    } catch (error) {
      console.error("Error fetching check-in history:", error);
      throw error;
    }
  },
};

export default teamService;
